import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { configurePdfUnicodeFont, PDF_UNICODE_FONT } from './pdfFont';
import { getLocale, getStoredLanguage, localizeJsPdfInstance, localizePdfText } from './localization';

interface QuotePdfOptions {
  quote: any;
  organization?: any;
  contact?: any;
  calculateSubtotal?: (item: any) => number;
  calculateTax?: (item: any) => number;
}

const PAGE_MARGIN = 14;
const FOOTER_SPACE = 18;
const DARK: [number, number, number] = [20, 20, 20];
const MUTED: [number, number, number] = [100, 100, 100];
const LIGHT: [number, number, number] = [245, 245, 244];

const numberValue = (value: unknown) => Number(value) || 0;

const parseTaxRate = (value: unknown) => {
  const match = String(value || '').match(/[0-9]+(?:\.[0-9]+)?/);
  return match ? numberValue(match[0]) / 100 : 0;
};

const calculateSubtotal = (item: any) => {
  const beforeDiscount = numberValue(item.quantity) * numberValue(item.unitPrice);
  const discount = item.discountType === 'amount'
    ? numberValue(item.discount)
    : beforeDiscount * numberValue(item.discount) / 100;
  return Math.max(0, beforeDiscount - discount);
};

const calculateTax = (item: any) => calculateSubtotal(item) * parseTaxRate(item.taxRate);
const money = (value: number) => numberValue(value).toLocaleString(getLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const date = (value: unknown) => value ? new Date(String(value)).toLocaleDateString(getLocale(), { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const lines = (value: unknown) => String(value || '').split(/\r?\n/).filter(Boolean);

export async function generateQuotePdf({ quote, organization, contact, calculateSubtotal: subtotalFn, calculateTax: taxFn }: QuotePdfOptions) {
  const subtotalFor = subtotalFn || calculateSubtotal;
  const taxFor = taxFn || calculateTax;
  const pdf = localizeJsPdfInstance(new jsPDF({ unit: 'mm', format: 'a4' }));
  await configurePdfUnicodeFont(pdf);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const right = pageWidth - PAGE_MARGIN;
  const currency = quote.currency || 'USD';
  const orgName = organization?.name || organization?.organizationName || 'Your Company';
  const orgAddress = organization?.address || organization?.billingAddress || '';
  const orgPhone = organization?.phone || organization?.phoneNumber || '';
  const orgEmail = organization?.email || '';
  const orgVat = organization?.vatNumber || organization?.vat || '';
  const customer = contact || {};
  const customerName = customer.name || quote.contactName || 'Valued Customer';
  const customerAddress = customer.address || quote.billingAddress || '';
  const customerEmail = customer.email || quote.contactEmail || '';
  const customerPhone = customer.phone || '';

  const footerPages = new Set<number>();
  const drawFooter = () => {
    const page = pdf.getNumberOfPages();
    if (footerPages.has(page)) return;
    footerPages.add(page);
    pdf.setDrawColor(...LIGHT);
    pdf.line(PAGE_MARGIN, pageHeight - 12, right, pageHeight - 12);
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(`${orgName} • Quote ${quote.number || ''}`, PAGE_MARGIN, pageHeight - 7);
    pdf.text(`Page ${page}`, right, pageHeight - 7, { align: 'right' });
  };

  const drawHeader = () => {
    pdf.setTextColor(...DARK);
    pdf.setFont(PDF_UNICODE_FONT, 'bold');
    pdf.setFontSize(18);
    pdf.text('QUOTATION', PAGE_MARGIN, 20);
    pdf.setFontSize(9);
    pdf.setTextColor(...MUTED);
    pdf.text(`Quote ${quote.number || quote.id || ''}`, PAGE_MARGIN, 27);
    pdf.setFont(PDF_UNICODE_FONT, 'normal');
    pdf.text(orgName, right, 17, { align: 'right' });
    const orgDetails = [orgAddress, orgPhone && `Phone: ${orgPhone}`, orgEmail && `Email: ${orgEmail}`, orgVat && `VAT: ${orgVat}`].flatMap(lines);
    pdf.text(orgDetails.slice(0, 4), right, 23, { align: 'right' });
    pdf.setDrawColor(...DARK);
    pdf.setLineWidth(0.5);
    pdf.line(PAGE_MARGIN, 34, right, 34);
  };

  drawHeader();
  pdf.setFont(PDF_UNICODE_FONT, 'normal');
  pdf.setTextColor(...DARK);
  pdf.setFontSize(9);

  const metaX = right - 66;
  pdf.setFont(PDF_UNICODE_FONT, 'bold');
  pdf.setTextColor(...MUTED);
  pdf.text('DATE', metaX, 44);
  pdf.text('EXPIRES', metaX, 51);
  if (quote.reference) pdf.text('REFERENCE', metaX, 58);
  pdf.setFont(PDF_UNICODE_FONT, 'normal');
  pdf.setTextColor(...DARK);
  pdf.text(date(quote.date), right, 44, { align: 'right' });
  pdf.text(date(quote.expiryDate || quote.dueDate), right, 51, { align: 'right' });
  if (quote.reference) pdf.text(String(quote.reference), right, 58, { align: 'right' });

  const infoTop = 43;
  pdf.setFont(PDF_UNICODE_FONT, 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  pdf.text('BILL TO', PAGE_MARGIN, infoTop);
  pdf.setFontSize(12);
  pdf.setTextColor(...DARK);
  pdf.text(customerName, PAGE_MARGIN, infoTop + 8);
  pdf.setFont(PDF_UNICODE_FONT, 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  const customerDetails = [customerAddress, customerPhone && `Phone: ${customerPhone}`, customerEmail && `Email: ${customerEmail}`].flatMap(lines);
  if (customerDetails.length) pdf.text(customerDetails.slice(0, 4), PAGE_MARGIN, infoTop + 15);

  const tableData = (quote.items || []).map((item: any) => {
    const itemSubtotal = numberValue(subtotalFor(item));
    const discount = item.discountType === 'amount' ? numberValue(item.discount) : numberValue(item.quantity) * numberValue(item.unitPrice) * numberValue(item.discount) / 100;
    return [
      String(item.description || item.name || item.item || 'Unnamed item'),
      String(numberValue(item.quantity)),
      `${currency} ${money(item.unitPrice)}`,
      discount > 0 ? `${currency} ${money(discount)}` : '—',
      `${currency} ${money(itemSubtotal + numberValue(taxFor(item)))}`,
    ];
  });

  autoTable(pdf, {
    startY: 72,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: FOOTER_SPACE },
    head: [[localizePdfText('Description'), localizePdfText('Qty'), localizePdfText('Unit price'), localizePdfText('Discount'), localizePdfText('Amount')]],
    body: tableData.length ? tableData : [[localizePdfText('No items'), '—', '—', '—', `${currency} 0.00`]],
    theme: 'grid',
    styles: { font: PDF_UNICODE_FONT, fontSize: 8.5, textColor: DARK, cellPadding: 3.5, lineColor: LIGHT, lineWidth: 0.2, valign: 'middle', halign: getStoredLanguage() === 'ar' ? 'right' : 'left' },
    headStyles: { fillColor: DARK, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8.5, halign: getStoredLanguage() === 'ar' ? 'right' : 'left' },
    alternateRowStyles: { fillColor: [250, 250, 249] },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 18, halign: 'right' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 34, halign: 'right' },
    },
    didDrawPage: () => drawFooter(),
  });

  let cursor = numberValue((pdf as any).lastAutoTable?.finalY) + 10;
  const subtotal = (quote.items || []).reduce((sum: number, item: any) => sum + numberValue(subtotalFor(item)), 0);
  const tax = (quote.items || []).reduce((sum: number, item: any) => sum + numberValue(taxFor(item)), 0);
  const total = numberValue(quote.totalAmount) || subtotal + tax;
  if (cursor + 34 > pageHeight - FOOTER_SPACE) {
    pdf.addPage();
    cursor = 20;
  }

  const totalX = right - 78;
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text('Subtotal', totalX, cursor);
  pdf.text(`${currency} ${money(subtotal)}`, right, cursor, { align: 'right' });
  pdf.text('Tax', totalX, cursor + 7);
  pdf.text(`${currency} ${money(tax)}`, right, cursor + 7, { align: 'right' });
  pdf.setFillColor(...DARK);
  pdf.roundedRect(totalX - 4, cursor + 12, 82, 13, 2, 2, 'F');
  pdf.setFont(PDF_UNICODE_FONT, 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text('TOTAL', totalX, cursor + 20);
  pdf.text(`${currency} ${money(total)}`, right - 4, cursor + 20, { align: 'right' });
  cursor += 34;

  const terms = quote.termsAndConditions || quote.notes;
  if (terms) {
    const termLines = pdf.splitTextToSize(String(terms), 92);
    const termHeight = 12 + termLines.length * 4;
    if (cursor + termHeight > pageHeight - FOOTER_SPACE) {
      pdf.addPage();
      cursor = 20;
    }
    pdf.setDrawColor(...LIGHT);
    pdf.line(PAGE_MARGIN, cursor, right, cursor);
    pdf.setFont(PDF_UNICODE_FONT, 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text('TERMS & CONDITIONS', PAGE_MARGIN, cursor + 8);
    pdf.setFont(PDF_UNICODE_FONT, 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...DARK);
    pdf.text(termLines, PAGE_MARGIN, cursor + 15);
  }

  drawFooter();
  pdf.save(`${quote.number || 'quote'}_Quotation.pdf`);
}
